import {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";

interface ModelInfoInputFieldProps {
  isInModal?: boolean;
  error?: boolean;
  columnName: string;
  initialValue: string;
  currentValue: string;
  onlyAllowNumbers: boolean;
  isRequired: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}

function ModelInfoInputField({
  isInModal = false,
  error,
  columnName,
  initialValue,
  currentValue,
  onlyAllowNumbers,
  isRequired,
  disabled,
  onChange,
}: ModelInfoInputFieldProps) {
  const [isModified, setIsModified] = useState(false);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [value, setValue] = useState(currentValue);
  const [width, setWidth] = useState<number>();

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const incomingValue = onlyAllowNumbers
      ? e.target.value.replace(/[^0-9]/g, "")
      : e.target.value;

    setValue(incomingValue);

    if (timerId) {
      clearTimeout(timerId);
    }

    setIsModified(incomingValue !== initialValue);

    const newTimerId = setTimeout(() => {
      onChange(incomingValue);
    }, 500);

    setTimerId(newTimerId);
  }

  useEffect(() => {
    setValue(currentValue);
    setIsModified(currentValue !== initialValue);
  }, [currentValue, initialValue]);

  const ref = useRef<HTMLInputElement>(null);

  var ctx = (document.createElement("canvas") as HTMLCanvasElement).getContext(
    "2d",
  );

  const calcWidth = useCallback(() => {
    if (ref?.current) {
      if (ctx) {
        const computedStyle = window.getComputedStyle(ref.current);
        ctx.font = computedStyle.font;
        const textSize = ctx.measureText(ref.current.value);

        return (
          textSize.width +
          parseFloat(computedStyle.paddingLeft) +
          parseFloat(computedStyle.paddingRight) +
          parseFloat(computedStyle.borderLeftWidth) +
          parseFloat(computedStyle.borderRightWidth)
        );
      }
    }

    return undefined;
  }, [ctx]);

  useLayoutEffect(() => {
    setWidth(calcWidth());
  }, [calcWidth]);

  return (
    <>
      <Form.Control
        ref={ref}
        className={isModified ? "border-3 border-warning" : ""}
        required={isRequired}
        name={columnName}
        value={value}
        size="sm"
        onChange={handleChange}
        style={
          isInModal ? { maxWidth: "105px" } : { width: "100%", minWidth: width }
        }
        type="text"
        disabled={disabled}
        isInvalid={(!currentValue && isRequired) || error}
      />
      {!error ?? (
        <Form.Control.Feedback type="invalid" tooltip>
          This field cannot be empty.
        </Form.Control.Feedback>
      )}
    </>
  );
}

export default ModelInfoInputField;
